<svelte:window on:keyup={onWindowKeyUp} on:resize={onWindowResize} />

<section
    bind:this={rootElement}
    class="prom-modal"
    on:click={closeHandler}
    role={rootElementRole}
    aria-labelledby={hideHeader ? null : headerClientId}
    aria-describedby={bodyClientId}
>
    <div
        class="prom-modal__dialog {scrollModiferClass}"
        role="document"
    >
        {#if !hideHeader}
            <header class="prom-modal__header">
                <h1 id="{headerClientId}" class="prom-modal__heading">{heading}</h1>
                {#if showCloseButton}
                <button
                    bind:this={closeElement}
                    class="prom-modal__close"
                    type="button"
                    on:click={closeHandler}
                >
                    <span class="screen-reader">{translations.CLOSE}</span>
                </button>
                {/if}
            </header>
        {/if}

        <div
            bind:this={bodyElement}
            id={bodyClientId}
            class="prom-modal__body"
        >
            <div class="prom-modal__body__content">
                {#if body}
                {@html body}
                {/if}

                <slot name="body"></slot>
            </div>
        </div>

        <footer class="prom-modal__footer">
            <slot name="footer"></slot>
        </footer>
    </div>

</section>

<script>
    import ModalEvent from './ModalEvents';

    import TabTrapHelper from '../../../../lib/helpers/TabTrapHelper';
    import { isIE, isEdge } from '../../../../lib/helpers/UAHelper';
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { debounce } from '@itslearning/atlas/dom/Ratecontrol';

    import { onMount, onDestroy, afterUpdate, createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    const defaultI18n = {
        CLOSE: 'Close'
    };

    /** @type {string} Used for header ({id}__label) and body ({id}__body) Optional, default value is 'prom-modal'. */
    export let id = 'prom-modal';
    /** @type {Object} */
    export let i18n = {};
    /** @type {string} The accessibility role attribute for the modal. */
    export let role = '';
    /** @type {string} The title of the modal, displayed as a heading tag.*/
    export let heading = '';
    /** @type {string} The content of the modal, i.e. message. You can also use the slots. Optional, better to use slot="body" */
    export let body = '';
    /** @type {boolean} Flag, which indicates that this modal dialog can be closable by a user. */
    export let closeable = true;
    /** @type {boolean} Flag, which indicates that this modal dialog has a close button. */
    export let showCloseButton = false;
    /** @type {boolean} Hide the dialog header. */
    export let hideHeader = false;

    let tabTrapper;
    let rootElement;
    let closeElement;
    let bodyElement;
    let scrollModiferClass;

    onMount(() => {
        tabTrapper = new TabTrapHelper(rootElement);
    });

    onDestroy(() => {
        tabTrapper.teardown();
    });

    afterUpdate(() => {
        setTimeout(() => {
            setScroll();
            tabTrapper.init();
        }, 0);
    })

    $: headerClientId = `${id}__label`;

    $: {
        if (!hideHeader && (!heading || typeof heading !== 'string')) {
            console.warn('`heading` must be supplied or `hideHeader` must be true');
        }
    }

    let rootElementRole;

    $: {
        const defaultRole = 'dialog';
        const roles = [defaultRole, 'alertdialog'];

        if (roles.indexOf(role) >= 0) {
            rootElementRole = role;
        } else {
            rootElementRole = defaultRole;
        }
    }

    $: translations = { ...defaultI18n, ...i18n };

    $: headerClientId = `${id}__label`;

    $: bodyClientId = `${id}__body`;

    /**
     * This function should only run for browsers that have buggy implementations of flex.
     * So, IE11 and Edge at this time. It should be called when `maybeStyleForScroll`
     * has finished running.
     */
    function maybeSetBodyHeight() {
        if (!isIE() && !isEdge()) {
            return;
        }

        // Due to the 'async' nature of this code, it is possible for any
        // synchronous code to have altered the DOM.
        if (!bodyElement) {
            return;
        }

        // This is a little bit of layout thrashing,
        // but it is debounced. In most use cases this is
        // easily good enough.
        bodyElement.style.height = 'auto';

        // The act of "getting" the client height is all that
        // seems to be required here. We don't need to use it.
        bodyElement.clientHeight; //eslint-disable-line

        bodyElement.style.height = '100%';
    }

    function maybeStyleForScroll() {
        // Due to the 'async' nature of this code, it is possible for any
        // synchronous code to have altered the DOM.
        if (!bodyElement) {
            return;
        }

        // +1 due to IE and Edge zoom rounding error.
        const hasScroll = bodyElement.scrollHeight > bodyElement.clientHeight + 1;
        scrollModiferClass = hasScroll ? 'prom-modal__dialog--has-scroll' : '';
    }

    function setScroll() {
        debounce(function () {
            maybeSetBodyHeight();
            maybeStyleForScroll();
        }, 100);
    }


    /**
     * @param {MouseEvent} event
     */
    function closeHandler(event) {
        const validCloseElements = [closeElement, rootElement];
        const shouldIgnore = validCloseElements.indexOf(event.target) < 0;

        if (shouldIgnore) {
            return;
        }

        if (event.target === closeElement) {
            event.stopPropagation();
        }

        close(event);
    }

    /**
     * @param {KeyboardEvent | MouseEvent} event
     */
    function close(event) {
        if (closeable) {
            dispatch(ModalEvent.CLOSED, { event });
        }
    }

    /**
     * @param {KeyboardEvent} event
     */
    function onWindowKeyUp(event) {
        const key = normalizeKey(event.key);

        if (key === Keys.ESCAPE) {
            dispatch(ModalEvent.CLOSED, { event });
        }
    }

    function onWindowResize() {
        setScroll();
    }
</script>
